import React, { useState, useEffect } from "react";
import { isEmail } from "../../utils";
import { toast } from "react-toastify";
import "../../style/customer-form.css";
import { useSelector } from "react-redux";

const CustomerForm = ({ customer, onSave, onCancel }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!customer;

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || "",
        email: customer.user_email || customer.email || "",
        phone: customer.mobile || customer.phone || "",
        address: customer.address || "",
      });
    }
  }, [customer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Email validation - required for both new and editing
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation - only if phone is provided
    if (formData.phone.trim()) {
      const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const cleanedData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
      };

      const result = await onSave(cleanedData);

      // Handle API validation errors
      if (result && result.error) {
        if (result.error.isValidationError) {
          // Handle field-specific validation errors
          if (result.error.validationErrors) {
            const fieldErrors = {};
            Object.keys(result.error.validationErrors).forEach((field) => {
              if (Array.isArray(result.error.validationErrors[field])) {
                fieldErrors[field] = result.error.validationErrors[field][0];
              } else {
                fieldErrors[field] = result.error.validationErrors[field];
              }
            });
            setErrors(fieldErrors);
            toast.error("Please check the form for errors.");
          } else {
            toast.error(
              result.error.message || "Please check the form for errors."
            );
          }
        } else {
          // Show backend error message
          const errorMessage =
            result.error.message ||
            "An error occurred while saving the customer. Please try again.";
          toast.error(errorMessage);
          console.error("Backend error:", result.error);
        }
        return; // Don't close form on error
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(
        "An error occurred while saving the customer. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="customer-form-modal">
      <div className="customer-form-modal-content no-scrollbar">
        <div className="customer-form-modal-header">
          <h5 className="customer-form-modal-title">
            {isEditing ? "Edit Customer" : "Add New Customer"}
          </h5>
          <p className="customer-form-modal-subtitle">
            {isEditing
              ? "Edit and update customer details below."
              : "Fill in the customer details below"}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="customer-form-modal-body">
            <div className="customer-form-field">
              <label htmlFor="name" className="customer-form-label">
                Customer Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`${isPartsURL ? "customer-form-input-parts" : isAccessoriesURL ? "customer-form-input-acc" : "customer-form-input"} ${
                  errors.name ? "is-invalid" : ""
                }`}
                placeholder="Enter customer name"
                required
              />
              {errors.name && (
                <span className="customer-form-error">{errors.name}</span>
              )}
            </div>

            <div className="customer-form-field">
              <label htmlFor="email" className="customer-form-label">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`${isPartsURL ? "customer-form-input-parts" : isAccessoriesURL ? "customer-form-input-acc" : "customer-form-input"} ${
                  errors.email ? "is-invalid" : ""
                }`}
                placeholder="Enter customer email address"
                required
              />
              {errors.email && (
                <span className="customer-form-error">{errors.email}</span>
              )}
            </div>

            <div className="customer-form-field">
              <label htmlFor="phone" className="customer-form-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`${isPartsURL ? "customer-form-input-parts" : isAccessoriesURL ? "customer-form-input-acc" : "customer-form-input"} ${
                  errors.phone ? "is-invalid" : ""
                }`}
                placeholder="Enter customer phone number"
              />
              {errors.phone && (
                <span className="customer-form-error">{errors.phone}</span>
              )}
            </div>

            <div className="customer-form-field">
              <label htmlFor="address" className="customer-form-label">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={`${isPartsURL ? "customer-form-textarea-parts" : isAccessoriesURL ? "customer-form-textarea-acc" : "customer-form-textarea"} ${
                  errors.address ? "is-invalid" : ""
                }`}
                placeholder="Enter complete address"
              ></textarea>
              {errors.address && (
                <span className="customer-form-error">{errors.address}</span>
              )}
            </div>
          </div>

          <div className="customer-form-modal-footer">
            <button
              type="button"
              className="customer-form-btn customer-form-btn-cancel"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${isPartsURL ? "customer-form-btn customer-form-btn-primary-parts" : isAccessoriesURL ? "customer-form-btn customer-form-btn-primary-acc" : "customer-form-btn customer-form-btn-primary"}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="customer-form-spinner"></span>
                  Saving...
                </>
              ) : isEditing ? (
                "Update Customer"
              ) : (
                "Add Customer"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
